// ===================================================
// GAM Command Center — Editor font registry
// The list the font picker shows, grouped by language.
// ===================================================

/**
 * A font the picker offers.
 *
 * `stack` is written verbatim into the `font-family` mark, so it must be a
 * complete CSS stack with a real fallback — a family that fails to load must
 * still land on something readable in the right script, not on the browser's
 * Latin default.
 *
 * `preview` renders the row's own label in the font itself, which is the only
 * honest way to show what the reader will get.
 */
export interface EditorFont {
  /** Stable id, used as the React key. */
  id: string;
  /** What the row says. Written in the script the font is for. */
  label: string;
  /** The full CSS font-family value. */
  stack: string;
}

export interface EditorFontGroup {
  /** Group heading, e.g. "עברית". */
  label: string;
  fonts: EditorFont[];
}

/**
 * Grouped by script, not by style, because that is the axis Gal asked for:
 * "רשימה של פונטים עם חלוקה לפי שפות עברית או אנגלית ובעתיד פונטים נוספים
 * לשפות אם יש בכך צורך". A third group is added by appending to this array —
 * nothing else in the picker needs to change.
 *
 * Every Hebrew family here is actually loaded (editor.css pulls Assistant,
 * Rubik and Secular One; Heebo and Frank Ruhl Libre come from the host app's
 * globals.css). A family nobody loads would fall through silently and the
 * picker would be a menu of one font wearing five names.
 */
export const EDITOR_FONT_GROUPS: EditorFontGroup[] = [
  {
    label: 'עברית',
    fonts: [
      { id: 'heebo', label: 'היבו', stack: '"Heebo", "Arial Hebrew", sans-serif' },
      { id: 'assistant', label: 'אסיסטנט', stack: '"Assistant", "Arial Hebrew", sans-serif' },
      { id: 'rubik', label: 'רוביק', stack: '"Rubik", "Arial Hebrew", sans-serif' },
      { id: 'frank', label: 'פרנק־רוהל', stack: '"Frank Ruhl Libre", "Times New Roman", serif' },
      { id: 'secular', label: 'סקולר וואן', stack: '"Secular One", "Arial Hebrew", sans-serif' },
    ],
  },
  {
    label: 'אנגלית',
    fonts: [
      { id: 'inter', label: 'Inter', stack: '"Inter", system-ui, sans-serif' },
      { id: 'geist', label: 'Geist', stack: '"Geist", system-ui, sans-serif' },
      { id: 'georgia', label: 'Georgia', stack: 'Georgia, "Times New Roman", serif' },
      { id: 'mono', label: 'Mono', stack: '"Geist Mono", ui-monospace, "Courier New", monospace' },
    ],
  },
];

/** Every font in every group, flattened — for lookups by stack. */
export const ALL_EDITOR_FONTS: EditorFont[] = EDITOR_FONT_GROUPS.flatMap((g) => g.fonts);
